import React, { useId, useMemo } from 'react';
import LineItemsList, { type LineColumn } from '../ui/LineItemsList';
import SearchableSelect from '../ui/SearchableSelect';
import TagMultiSelect from '../ui/TagMultiSelect';
import { freeProjects, newLine, projectRef, type RoleLine } from './personLines';
import type { Workspace } from '../../utils/workspaces';

interface PersonRolesSectionProps {
  lines: readonly RoleLine[];
  onChange: (lines: RoleLine[]) => void;
  /** The workspace's projects, for the Project select of a new line. */
  projects: readonly Workspace[];
  knownTeams: readonly string[];
  errors: Readonly<Record<string, string>>;
  disabled: boolean;
}

/** The person's job on each project: one line per project, saved with the page. */
const PersonRolesSection: React.FC<PersonRolesSectionProps> = ({ lines, onChange, projects, knownTeams, errors, disabled }) => {
  const uid = useId();
  const patch = (key: string, change: Partial<RoleLine>) => onChange(lines.map(l => (l.key === key ? { ...l, ...change } : l)));
  const free = useMemo(() => freeProjects(projects, lines), [projects, lines]);

  const columns: LineColumn<RoleLine>[] = [
    {
      key: 'project',
      header: 'Project',
      render: (line, ctx) => line.teamMemberId ? (
        <div className="epl-project">
          <strong className="epl-project__name">{line.projectName}</strong>
          <span className="epl-project__ref">{projectRef(line.projectId)}</span>
          {line.loadError && <span className="epl-project__note" role="status">{line.loadError}</span>}
        </div>
      ) : (
        <SearchableSelect
          value={line.projectId}
          placeholder="Choose a project"
          ariaLabel={ctx.ariaLabel}
          disabled={ctx.disabled}
          onChange={projectId => {
            const chosen = projects.find(p => p.project_id === projectId);
            patch(line.key, { projectId, projectName: chosen?.project_name ?? '' });
          }}
          options={freeProjects(projects, lines, line.key).map(p => ({ value: p.project_id, label: `${p.project_name} (${projectRef(p.project_id)})` }))}
        />
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (line, ctx) => (
        <input
          type="text"
          maxLength={100}
          value={line.role}
          placeholder="e.g. Day coordinator"
          aria-label={ctx.ariaLabel}
          aria-describedby={ctx.errorId}
          disabled={ctx.disabled || !!line.loadError}
          onChange={e => patch(line.key, { role: e.target.value })}
        />
      ),
    },
    {
      key: 'active',
      header: 'Status',
      render: (line, ctx) => (
        <label className="epl-check">
          <input
            type="checkbox"
            checked={line.isActive}
            aria-label={`Active, ${line.projectName || 'new line'}`}
            disabled={ctx.disabled || !!line.loadError}
            onChange={e => patch(line.key, { isActive: e.target.checked })}
          />
          <span>Active on this project</span>
        </label>
      ),
    },
    {
      key: 'teams',
      header: 'Teams',
      wide: true,
      render: (line, ctx) => (
        <TagMultiSelect
          values={line.teams}
          options={[...knownTeams]}
          placeholder="Pick or add a team"
          disabled={ctx.disabled}
          onChange={teams => patch(line.key, { teams })}
        />
      ),
    },
    {
      key: 'responsibilities',
      header: 'Responsibilities (one per line)',
      wide: true,
      render: (line, ctx) => (
        <textarea
          rows={2}
          value={line.responsibilities}
          aria-label={ctx.ariaLabel}
          disabled={ctx.disabled || !!line.loadError}
          onChange={e => patch(line.key, { responsibilities: e.target.value })}
        />
      ),
    },
  ];

  const addReason = projects.length === 0
    ? 'This workspace has no projects yet.'
    : free.length === 0 ? 'This person is already listed on every project.' : null;

  return (
    <section className="settings-card ep-section" aria-labelledby={`${uid}-title`}>
      <h2 id={`${uid}-title`} className="ap-section-title">Roles on projects</h2>
      <p className="settings-muted-xs">
        What this person does on each project. Changes are saved with the page. Taking a line off removes them from
        that project&apos;s team; the entry goes to the project&apos;s trash and can be restored.
      </p>
      <LineItemsList<RoleLine>
        label="Roles on projects"
        lines={lines}
        getKey={l => l.key}
        lineName={l => l.projectName}
        columns={columns}
        errors={errors}
        disabled={disabled}
        addLabel="Add project"
        addDisabledReason={addReason}
        onAdd={() => onChange([...lines, newLine()])}
        onRemove={line => onChange(lines.filter(l => l.key !== line.key))}
        emptyTitle="Not on any project yet"
        emptyHint={disabled ? undefined : 'Use Add project to put them on a project team.'}
      />
    </section>
  );
};

export default PersonRolesSection;
